import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  History, Plus, MessageSquare, FileText, RefreshCw, User,
  AlertTriangle, StickyNote, Phone, Mail, Calendar, Shield,
  Lock, Send, ClipboardCheck, LayoutTemplate, ChevronDown, ChevronUp,
  ArrowRightLeft
} from 'lucide-react';

const EVENT_CONFIG = {
  CREACION:             { color: 'bg-green-500',  label: 'Creación',            labelBg: 'bg-green-50 text-green-700 border-green-200',   Icon: Plus },
  COMENTARIO:           { color: 'bg-blue-500',   label: 'Comentario',          labelBg: 'bg-blue-50 text-blue-700 border-blue-200',      Icon: MessageSquare },
  NOTA:                 { color: 'bg-pink-500',    label: 'Nota',                labelBg: 'bg-pink-50 text-pink-700 border-pink-200',      Icon: StickyNote },
  DOCUMENTO:            { color: 'bg-orange-500',  label: 'Documento',           labelBg: 'bg-orange-50 text-orange-700 border-orange-200', Icon: FileText },
  CAMBIO_ESTADO:        { color: 'bg-purple-500',  label: 'Cambio de Estado',    labelBg: 'bg-purple-50 text-purple-700 border-purple-200', Icon: RefreshCw },
  ASIGNACION:           { color: 'bg-cyan-500',    label: 'Asignación',          labelBg: 'bg-cyan-50 text-cyan-700 border-cyan-200',      Icon: User },
  ALERTA_AGREGADA:      { color: 'bg-amber-500',   label: 'Alerta Agregada',     labelBg: 'bg-amber-50 text-amber-700 border-amber-200',   Icon: AlertTriangle },
  SOLICITUD_CREADA:     { color: 'bg-indigo-500',  label: 'Solicitud Generada',  labelBg: 'bg-indigo-50 text-indigo-700 border-indigo-200', Icon: ClipboardCheck },
  SOLICITUD_APROBACION: { color: 'bg-indigo-500',  label: 'Solicitud Aprobación', labelBg: 'bg-indigo-50 text-indigo-700 border-indigo-200', Icon: ClipboardCheck },
  EMAIL_ENVIADO:        { color: 'bg-teal-500',    label: 'Email Enviado',       labelBg: 'bg-teal-50 text-teal-700 border-teal-200',      Icon: Send },
  EMAIL_REENVIADO:     { color: 'bg-teal-400',    label: 'Email Reenviado',     labelBg: 'bg-teal-50 text-teal-700 border-teal-200',      Icon: Send },
  BLOQUEO_WAYA:         { color: 'bg-red-500',     label: 'Bloqueo de Cuenta',   labelBg: 'bg-red-50 text-red-700 border-red-200',         Icon: Lock },
  ASIGNADO_COMPLIANCE:  { color: 'bg-violet-600',  label: 'Asignado a Compliance', labelBg: 'bg-violet-50 text-violet-700 border-violet-200', Icon: Shield },
  TEMPLATE_APLICADA:    { color: 'bg-slate-500',   label: 'Plantilla Aplicada',  labelBg: 'bg-slate-50 text-slate-700 border-slate-200',   Icon: LayoutTemplate },
  SOLICITUD_VINCULADA:  { color: 'bg-indigo-400',  label: 'Solicitud Vinculada', labelBg: 'bg-indigo-50 text-indigo-600 border-indigo-200', Icon: ClipboardCheck },
  SOLICITUD_DESVINCULADA: { color: 'bg-gray-400',  label: 'Solicitud Desvinculada', labelBg: 'bg-gray-50 text-gray-600 border-gray-200',  Icon: ClipboardCheck },
  NOTA_ALERTA:          { color: 'bg-sky-500',     label: 'Nota en Alerta',      labelBg: 'bg-sky-50 text-sky-700 border-sky-200',        Icon: MessageSquare },
  UPDATE:               { color: 'bg-sky-500',     label: 'Actualización',       labelBg: 'bg-sky-50 text-sky-700 border-sky-200',        Icon: ArrowRightLeft },
};

const DEFAULT_CONFIG = { color: 'bg-slate-400', label: 'Evento', labelBg: 'bg-slate-50 text-slate-600 border-slate-200', Icon: History };

function formatDate(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString('es-AR'); } catch { return ts; }
}

const CasoTimeline = ({ caso }) => {
  const [apiEvents, setApiEvents] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (caso?.id) loadApiEvents();
  }, [caso?.id]);

  const loadApiEvents = async () => {
    try {
      const res = await api.get(`/cases/${caso.id}/timeline`);
      setApiEvents(res.data || []);
    } catch { /* ignore */ }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setAdding(true);
    try {
      await api.post(`/cases/${caso.id}/timeline`, {
        tipo: 'NOTA',
        descripcion: newNote.trim()
      });
      toast.success('Nota agregada al timeline');
      setNewNote('');
      setShowAdd(false);
      loadApiEvents();
    } catch {
      toast.error('Error al agregar nota');
    } finally {
      setAdding(false);
    }
  };

  // Merge embedded timeline + API activity logs (deduplicated by id)
  const embeddedEvents = (caso.timeline || []).map(ev => ({
    ...ev,
    _ts: ev.fecha || ev.timestamp || '',
    _source: 'embedded'
  }));

  const normalizedApiEvents = apiEvents.map(ev => ({
    ...ev,
    _ts: ev.timestamp || ev.fecha || '',
    _source: 'api'
  }));

  // Deduplicate by id, prefer embedded
  const seenIds = new Set();
  const allEvents = [];
  for (const ev of embeddedEvents) {
    if (ev.id) seenIds.add(ev.id);
    allEvents.push(ev);
  }
  for (const ev of normalizedApiEvents) {
    if (ev.id && seenIds.has(ev.id)) continue;
    seenIds.add(ev.id);
    allEvents.push(ev);
  }

  // Sort newest first
  allEvents.sort((a, b) => {
    const ta = new Date(a._ts || 0).getTime();
    const tb = new Date(b._ts || 0).getTime();
    return tb - ta;
  });

  const displayEvents = showAll ? allEvents : allEvents.slice(0, 15);

  return (
    <div data-testid="caso-timeline">
      {/* Header with add note button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History size={16} className="text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">Timeline de Actividad</span>
          <Badge variant="secondary" className="text-xs">{allEvents.length} eventos</Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAdd(true)}
          className="text-xs"
          data-testid="add-timeline-note"
        >
          <Plus size={14} className="mr-1" />
          Agregar Nota
        </Button>
      </div>

      {/* Timeline visual */}
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
        <div className="space-y-4">
          {displayEvents.map((evento, idx) => {
            let cfg = EVENT_CONFIG[evento.tipo] || EVENT_CONFIG[evento.accion];

            if (evento.tipo === 'NOTA' && evento.metadata?.tipo_nota === 'LLAMADA') {
              cfg = { ...EVENT_CONFIG.NOTA, Icon: Phone };
            } else if (evento.tipo === 'NOTA' && evento.metadata?.tipo_nota === 'EMAIL') {
              cfg = { ...EVENT_CONFIG.NOTA, Icon: Mail };
            }

            if (!cfg) cfg = DEFAULT_CONFIG;
            const { color, label, labelBg, Icon } = cfg;

            return (
              <div key={evento.id || idx} className="relative flex gap-4 pl-10 group" data-testid={`timeline-event-${evento.tipo || 'unknown'}`}>
                <div className={`absolute left-2 w-5 h-5 rounded-full ${color} flex items-center justify-center ring-2 ring-white shadow-sm transition-transform group-hover:scale-110`}>
                  <Icon size={12} className="text-white" />
                </div>
                <div className="flex-1 pb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${labelBg}`}>
                      <Icon size={10} /> {label}
                    </span>
                    {evento._source === 'api' && evento.source === 'auto' && (
                      <span className="text-[10px] text-slate-300 px-1.5 py-0.5 rounded bg-slate-50">auto</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-800 mt-1">{evento.descripcion}</p>
                  {/* Email content preview */}
                  {(evento.tipo === 'EMAIL_ENVIADO' || evento.tipo === 'EMAIL_REENVIADO') && evento.metadata?.contenido_texto && (
                    <details className="mt-2 text-xs border rounded-lg border-teal-200 bg-teal-50/50">
                      <summary className="cursor-pointer px-3 py-1.5 text-teal-700 font-medium hover:bg-teal-50">
                        Ver contenido del email — Para: {evento.metadata.destinatario} | Asunto: {evento.metadata.asunto}
                      </summary>
                      <div className="px-3 py-2 border-t border-teal-200 text-slate-700 whitespace-pre-wrap">
                        {evento.metadata.contenido_texto}
                      </div>
                      {evento.metadata.bcc?.length > 0 && (
                        <div className="px-3 py-1 text-[10px] text-slate-400 border-t border-teal-100">
                          CCO: {evento.metadata.bcc.join(', ')}
                        </div>
                      )}
                    </details>
                  )}
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                    <span className="flex items-center gap-1">
                      <User size={11} />
                      {evento.usuario || evento.usuario_email || ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={11} />
                      {formatDate(evento.fecha || evento.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {allEvents.length > 15 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full text-center text-xs text-blue-600 hover:text-blue-700 py-2 mt-2 transition-colors"
          >
            {showAll ? (
              <span className="flex items-center justify-center gap-1"><ChevronUp size={14} /> Mostrar menos</span>
            ) : (
              <span className="flex items-center justify-center gap-1"><ChevronDown size={14} /> Ver {allEvents.length - 15} más</span>
            )}
          </button>
        )}
      </div>

      {/* Add Note Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md" aria-describedby="add-timeline-note-desc">
          <DialogHeader>
            <DialogTitle>Agregar Nota al Timeline</DialogTitle>
          </DialogHeader>
          <p id="add-timeline-note-desc" className="text-sm text-slate-500 mb-3">Escribe una nota o comentario para el timeline del caso.</p>
          <textarea
            data-testid="timeline-note-input"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Escribir nota..."
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>Cancelar</Button>
            <Button
              size="sm"
              onClick={addNote}
              disabled={adding || !newNote.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="timeline-note-submit"
            >
              {adding ? 'Guardando...' : 'Guardar Nota'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CasoTimeline;
